/**
 * Order guide data (TASK-0229, INC-1a). Read-only: nothing here writes.
 *
 * Keys sit under `poKeys.all` (`['purchase-orders', 'guide', …]`), so every
 * existing PO invalidation (receive, cancel, a new PO) refreshes the guide too.
 * A refetch never touches the quantities typed on the vendor guide: those live
 * in component state and are hydrated once per vendor and store day.
 *
 * One guide costs about a second of database time and the server lets only two
 * run at once, so results stay fresh for two minutes, are not refetched on
 * window focus, and a 503 (busy or too slow) is shown in place with Retry
 * instead of being retried automatically.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api/client';
import type { Dispatch } from '@/features/buying/dispatch/api';
import { asText } from '@/lib/searchParams';
import type { PurchaseOrder } from '@/lib/api/types';
import type {
  Board,
  BoardSearch,
  FixRow,
  Calibration,
  GuideDraft,
  GuideSearch,
  ListKind,
  ListPage,
  MoveLineResult,
  NeedsPage,
  ProductSources,
  RhythmOutcome,
  SendChannel,
  SharedRow,
  UnsourcedRow,
  VendorGuide,
  VendorGuideSearch,
} from './types';

export const guideKeys = {
  all: ['purchase-orders', 'guide'] as const,
  board: (params: Record<string, unknown>) =>
    ['purchase-orders', 'guide', 'board', params] as const,
  vendor: (vendorId: number, params: Record<string, unknown>) =>
    ['purchase-orders', 'guide', 'vendor', vendorId, params] as const,
  // TASK-0249 (INC-1c)
  product: (productId: number, params: Record<string, unknown>) =>
    ['purchase-orders', 'guide', 'product', productId, params] as const,
  needs: (params: Record<string, unknown>) =>
    ['purchase-orders', 'guide', 'needs', params] as const,
  lists: (params: Record<string, unknown>) =>
    ['purchase-orders', 'guide', 'lists', params] as const,
  calibration: (weeks: number) =>
    ['purchase-orders', 'guide', 'calibration', weeks] as const,
};

export const GUIDE_STALE_MS = 2 * 60_000;

/** Busy (two guides already running) and too slow both answer 503. */
export function isGuideUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503;
}

/** A refusal or a 503 is shown at once; only a dropped connection is retried, once. */
function retry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400) return false;
  return failureCount < 1;
}

/* ── URL search params ───────────────────────────────────────────────────── */

const EXCLUDE_WORDS = ['auto', 'low', 'none'] as const;

/**
 * `?exclude=` as the API takes it: a mode word or a list of week numbers 0-15.
 * TanStack Router JSON-parses the URL, so `?exclude=2` arrives as the number 2.
 * Anything else is dropped (the store default applies).
 */
export function readExclude(value: unknown): string | undefined {
  const text = asText(value).trim().toLowerCase();
  if (text === '') return undefined;
  if ((EXCLUDE_WORDS as readonly string[]).includes(text)) return text;
  const parts = text.split(',').map((part) => part.trim());
  if (parts.every((part) => /^\d{1,2}$/.test(part) && Number(part) <= 15)) {
    return [...new Set(parts.map(Number))].sort((a, b) => a - b).join(',');
  }
  return undefined;
}

/** `?factor=` between 0.5 and 3; anything else is dropped (1 applies). */
export function readFactor(value: unknown): number | undefined {
  const text = asText(value).trim();
  if (text === '') return undefined;
  const n = Number(text);
  return Number.isFinite(n) && n >= 0.5 && n <= 3 ? n : undefined;
}

function readQuery(value: unknown): string | undefined {
  const text = asText(value);
  return text === '' ? undefined : text;
}

/** A positive id from the URL (TanStack Router may hand over a number or a string). */
export function readId(value: unknown): number | undefined {
  const n = Number(asText(value).trim());
  return Number.isInteger(n) && n > 0 && n <= 2147483647 ? n : undefined;
}

function readPage(value: unknown): number | undefined {
  const n = readId(value);
  return n !== undefined && n > 1 ? n : undefined;
}

export function validateBoardSearch(s: Record<string, unknown>): BoardSearch {
  return {
    scope: s.scope === 'active' ? 'active' : undefined,
    exclude: readExclude(s.exclude),
    factor: readFactor(s.factor),
    q: readQuery(s.q),
    // TASK-0249 (INC-1c): By product and the lists are views of the board.
    view: s.view === 'products' || s.view === 'lists' ? s.view : undefined,
    groupBy: s.groupBy === 'vendor' ? 'vendor' : undefined,
    show: s.show === 'to_order' || s.show === 'check' ? s.show : undefined,
    page: readPage(s.page),
    list: s.list === 'unsourced' || s.list === 'data_to_fix' ? s.list : undefined,
    all:
      s.all === true || s.all === 'true' || s.all === 1 || s.all === '1'
        ? true
        : undefined,
    product: readId(s.product),
  };
}

export function validateVendorGuideSearch(s: Record<string, unknown>): VendorGuideSearch {
  return {
    show: s.show === 'all' ? 'all' : undefined,
    exclude: readExclude(s.exclude),
    factor: readFactor(s.factor),
    q: readQuery(s.q),
    product: readId(s.product),
  };
}

/** The sales-basis params a link between the two screens carries along. */
export function basisSearch(search: GuideSearch): GuideSearch {
  return {
    ...(search.exclude !== undefined ? { exclude: search.exclude } : {}),
    ...(search.factor !== undefined ? { factor: search.factor } : {}),
  };
}

/* ── Queries ─────────────────────────────────────────────────────────────── */

/** TASK-0251 (INC-4): suggestions vs ordered vs received, per vendor, over the last 4 or 8 weeks. */
export function useCalibration(weeks: 4 | 8) {
  return useQuery({
    queryKey: guideKeys.calibration(weeks),
    queryFn: () =>
      api.get<Calibration>('/purchasing/order-guide/calibration', { params: { weeks } }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    placeholderData: keepPreviousData,
  });
}

export function useBoard(search: BoardSearch, enabled = true) {
  const params = {
    ...basisSearch(search),
    ...(search.scope ? { scope: search.scope } : {}),
  };
  return useQuery({
    queryKey: guideKeys.board(params),
    queryFn: () => api.get<Board>('/purchasing/order-guide', { params }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVendorGuide(vendorId: number, search: VendorGuideSearch) {
  const params = {
    ...basisSearch(search),
    ...(search.show ? { show: search.show } : {}),
  };
  return useQuery({
    queryKey: guideKeys.vendor(vendorId, params),
    queryFn: () =>
      api.get<VendorGuide>(`/purchasing/order-guide/vendors/${vendorId}`, { params }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    // A change of weeks, factor or view keeps the grid on screen while the
    // new numbers load; the typed quantities never depended on it anyway.
    placeholderData: (previous) =>
      previous && previous.vendor.id === vendorId ? previous : undefined,
    enabled: Number.isInteger(vendorId) && vendorId > 0,
  });
}

/** The store name for the order text. Missing is fine: the line is left out. */
export function useStoreName(): string | null {
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string | null>>('/settings'),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
  const name = settings.data?.store_name?.trim();
  return name ? name : null;
}

/** The blind count sheet (CSV), for the whole board or one vendor. */
export async function downloadCountSheet(search: GuideSearch, vendorId?: number) {
  return api.download('/purchasing/order-guide/count-first', {
    params: {
      format: 'csv',
      ...basisSearch(search),
      ...(vendorId ? { vendorId } : {}),
    },
    timeout: 30_000,
  });
}

/* ── Answering a "deliveries look missing" flag (TASK-0239) ──────────────── */

export interface ResolveInput {
  outcome: RhythmOutcome;
  /** The last quiet day; for "no delivery expected" only. */
  quietUntil?: string;
  note?: string;
}

/**
 * Records the buyer's answer. The guide is read-only everywhere else; this is
 * the one write on these screens, and it refreshes both of them so the flag
 * clears (or reads "Acknowledged") on the next paint.
 */
export function useResolveRhythm(vendorId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResolveInput) =>
      api.post(`/purchasing/vendors/${vendorId}/rhythm-resolution`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: guideKeys.all });
    },
  });
}

/* ── The guide's saved order and Send (TASK-0241, INC-1b) ────────────────── */

/** One line as the draft and Send take it: base units, the pack, the estimates, the suggestion. */
export interface GuideLineBody {
  productId: number;
  unit: 'ea' | 'lb';
  orderedQty: number;
  orderedPacks?: number | null;
  packSize?: number | null;
  estUnitCostCents?: number | null;
  estPackCostCents?: number | null;
  suggestion?: Record<string, unknown> | null;
}

export interface GuideParamsBody {
  asOf: string;
  exclude: number[];
  factor: number;
  leadDays: number | null;
  safetyWeeks: number;
}

export interface SaveDraftBody {
  expectedUpdatedAt: string | null;
  basisAt: string;
  params: GuideParamsBody;
  lines: GuideLineBody[];
}

export interface SendBody {
  clientRequestId: string;
  expectedUpdatedAt: string | null;
  basisAt: string;
  basis: Array<{ productId: number; inbound: number }>;
  expectedOn: string;
  sentVia: SendChannel;
  placedBy: string;
  notes?: string;
  lines: GuideLineBody[];
  acknowledgeOverlap?: number[];
  /** TASK-0254 (VO-2): the preview the buyer confirmed; the server records exactly it. */
  dispatch?: {
    kind: 'original';
    format: 'list' | 'csv_file' | 'csv_body';
    renderHash: string;
    purchaseOrderId: number | null;
    recipients: { to: string[]; cc: string[] };
    subject: string;
    body: string;
  };
}

export interface SendResult {
  purchaseOrder: PurchaseOrder;
  replayed: boolean;
  /** TASK-0254 (VO-2): the dispatch this Send recorded (null without a `dispatch`). */
  dispatch?: Dispatch | null;
}

export interface OverlapPo {
  poId: number;
  vendorName: string | null;
  status: string;
  openQty: string;
}

export interface Overlap {
  productId: number;
  name: string;
  basisInbound: string;
  currentInbound: string;
  pos: OverlapPo[];
}

export function saveGuideDraft(
  vendorId: number,
  body: SaveDraftBody
): Promise<GuideDraft> {
  return api.put<GuideDraft>(`/purchasing/order-guide/vendors/${vendorId}/draft`, body);
}

export function discardGuideDraft(vendorId: number, expectedUpdatedAt: string) {
  return api.post<{ purchaseOrderId: number; discarded: boolean }>(
    `/purchasing/order-guide/vendors/${vendorId}/draft/discard`,
    { expectedUpdatedAt }
  );
}

export function sendGuideOrder(vendorId: number, body: SendBody): Promise<SendResult> {
  return api.post<SendResult>(`/purchasing/order-guide/vendors/${vendorId}/send`, body);
}

/** The refusal's code, when the API answered with one. */
export function refusalCodeOf(error: unknown): string | null {
  return error instanceof ApiError ? (error.body?.code ?? null) : null;
}

function refusalDetails(error: unknown): Record<string, unknown> | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.body?.details ?? error.details;
  return details && typeof details === 'object' && !Array.isArray(details)
    ? (details as Record<string, unknown>)
    : null;
}

/** The overlaps of a 409 OVERLAP, else null. */
export function overlapsFromError(error: unknown): Overlap[] | null {
  if (refusalCodeOf(error) !== 'OVERLAP') return null;
  const overlaps = refusalDetails(error)?.overlaps;
  return Array.isArray(overlaps) ? (overlaps as unknown as Overlap[]) : [];
}

/** A 409 STALE_DRAFT: why, and the PO when the server named one. */
export function staleDraftFromError(error: unknown): {
  reason: 'changed' | 'sent' | 'discarded';
  purchaseOrderId: number | null;
  message: string;
} | null {
  if (refusalCodeOf(error) !== 'STALE_DRAFT') return null;
  const details = refusalDetails(error) ?? {};
  const reason =
    details.reason === 'sent' || details.reason === 'discarded'
      ? details.reason
      : 'changed';
  const po = Number(details.purchaseOrderId);
  return {
    reason,
    purchaseOrderId: Number.isInteger(po) && po > 0 ? po : null,
    message:
      error instanceof Error ? error.message : 'This order changed. Reload the guide.',
  };
}

/** Names used on recent guide orders, newest first: suggestions for Placed by. */
export function useRecentPlacedBy(): string[] {
  const recent = useQuery({
    queryKey: ['purchase-orders', 'placed-by'],
    queryFn: () =>
      api.get<{ data: Array<{ placedBy?: string | null }> }>('/vendors/purchase-orders', {
        params: {
          origin: 'order_guide',
          status: 'submitted,partially_received,received',
          pageSize: 50,
        },
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const names: string[] = [];
  for (const row of recent.data?.data ?? []) {
    const name = row.placedBy?.trim();
    if (name && !names.includes(name)) names.push(name);
  }
  return names;
}

/* ── INC-1c (TASK-0249): Sources, moves, By product, lists ───────────────── */

/** Every vendor of one product, side by side (the Sources drawer). */
export function useProductSources(productId: number | null, basis: GuideSearch) {
  const params: Record<string, string | number> = { ...basisSearch(basis) };
  return useQuery({
    queryKey: guideKeys.product(productId ?? 0, params),
    queryFn: () =>
      api.get<ProductSources>(`/purchasing/order-guide/products/${productId}`, {
        params,
      }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    enabled: productId !== null && productId > 0,
  });
}

export interface MoveLineBody {
  productId: number;
  /** Pieces or pounds; the server rounds it to the target vendor's case size. */
  baseQty: number;
  suggestion?: Record<string, unknown> | null;
  fromVendorId?: number;
  fromDraftUpdatedAt?: string;
  targetDraftUpdatedAt?: string | null;
  /**
   * The Sources drawer's put (review F9): it saw the product on no guide order, so the server refuses it
   * (409 PLANNED_ELSEWHERE, or STALE_DRAFT for this vendor's own) when one has it by now.
   */
  expectUnplanned?: true;
}

/** One line onto a vendor's saved order (a move with fromVendorId). */
export function moveGuideLine(vendorId: number, body: MoveLineBody) {
  return api.post<MoveLineResult>(
    `/purchasing/order-guide/vendors/${vendorId}/draft/lines`,
    body
  );
}

/*
 * Line writes this tab has queued or sent, by product (review F9). By product queues a box's write
 * behind the earlier writes to the same vendor, so for a while the server does not have it yet; the
 * Sources drawer waits for a product's writes before it plans "Order from X instead", and never plans on
 * an order one of them is still changing.
 */
const lineWrites = new Map<number, Set<Promise<unknown>>>();

/** Register a queued or running write of `productId`'s line; it is forgotten once it settles. */
export function trackLineWrite<T>(productId: number, write: Promise<T>): Promise<T> {
  let writes = lineWrites.get(productId);
  if (!writes) {
    writes = new Set();
    lineWrites.set(productId, writes);
  }
  const set = writes;
  set.add(write);
  const forget = () => {
    set.delete(write);
    if (set.size === 0 && lineWrites.get(productId) === set) lineWrites.delete(productId);
  };
  write.then(forget, forget);
  return write;
}

/** Resolves once no write of `productId`'s line is queued or running (failed ones count as done). */
export async function lineWritesSettled(productId: number): Promise<void> {
  for (;;) {
    const writes = lineWrites.get(productId);
    if (!writes || writes.size === 0) return;
    await Promise.allSettled([...writes]);
  }
}

/** The buyer's one-click pack confirmation (audited vendor_item.update). */
export function confirmPack(vendorId: number, productId: number) {
  return api.put(`/vendors/${vendorId}/items/${productId}`, { packConfirmed: true });
}

/** "Make X the usual source": the existing audited preferred flag. */
export function makeUsualSource(vendorId: number, productId: number) {
  return api.put(`/vendors/${vendorId}/items/${productId}`, { isPreferred: true });
}

export const NEEDS_PAGE_SIZE = 100;

export function needsParams(search: BoardSearch): Record<string, string | number> {
  return {
    ...basisSearch(search),
    ...(search.scope ? { scope: search.scope } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.show ? { show: search.show } : {}),
    ...(search.groupBy ? { groupBy: search.groupBy } : {}),
    page: search.page ?? 1,
    pageSize: NEEDS_PAGE_SIZE,
  };
}

/** By product: one row per product across vendors, paged from the server's one computation. */
export function useNeeds(search: BoardSearch, enabled = true) {
  const params = needsParams(search);
  return useQuery({
    queryKey: guideKeys.needs(params),
    queryFn: () => api.get<NeedsPage>('/purchasing/order-guide/needs', { params }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export const LIST_PAGE_SIZE = 100;

type ListRow<K extends ListKind> = K extends 'shared'
  ? SharedRow
  : K extends 'unsourced'
    ? UnsourcedRow
    : FixRow;

export function listParams(kind: ListKind, search: BoardSearch) {
  return {
    kind,
    weeklyOnly: search.all ? 'false' : 'true',
    page: search.page ?? 1,
    pageSize: LIST_PAGE_SIZE,
  };
}

export function useList<K extends ListKind>(
  kind: K,
  search: BoardSearch,
  enabled = true
) {
  const params = listParams(kind, search);
  return useQuery({
    queryKey: guideKeys.lists(params),
    queryFn: () =>
      api.get<ListPage<ListRow<K>>>('/purchasing/order-guide/lists', { params }),
    staleTime: GUIDE_STALE_MS,
    refetchOnWindowFocus: false,
    retry,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export async function downloadList(kind: ListKind, search: BoardSearch) {
  return api.download('/purchasing/order-guide/lists', {
    params: { kind, format: 'csv', weeklyOnly: search.all ? 'false' : 'true' },
    timeout: 30_000,
  });
}
